/**
 * Actycrea: forbes-video.esm.js (ES6)
 * MIT
 */

import { PREFIX } from '../helpers/config.js';

/* =========================
 * Constantes de configuración
 * =======================*/

const SELECTOR_DATA   = `[data-${PREFIX ?? 'wphack'}-forbes-video]`;
const SELECTOR_OPEN   = `[data-${PREFIX ?? 'wphack'}-forbes-video-open]`;
const SELECTOR_CLOSE  = `[data-${PREFIX ?? 'wphack'}-forbes-video-close]`;
const SELECTOR_TOGGLE = `[data-${PREFIX ?? 'wphack'}-forbes-video-toggle]`;
const SELECTOR_MUTE   = `[data-${PREFIX ?? 'wphack'}-forbes-video-mute]`;

const CLASS_OPEN      = 'is-open';
const CLASS_PLAYING   = 'is-playing';
const CLASS_MUTED     = 'is-muted';
const CLASS_HTML_OPEN = 'forbes-video-open';

// Margen de seguridad por si no llega el transitionend al cerrar.
const CLOSE_FALLBACK  = 600;

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Cada tarjeta abre su propio <dialog> modal (showModal): el navegador se
 * encarga del foco atrapado, del fondo inerte y de la tecla Esc. Aquí solo
 * se gestiona la animación de apertura/cierre y el reproductor.
 */
export class ForbesVideo {
  /**
   * @param {HTMLElement} element lista con data-forbes-video
   */
  constructor(element) {
    if (!(element instanceof HTMLElement)) throw new Error('ForbesVideo: element inválido');

    this._element = element;
    this._labels  = {
      play:   element.dataset.labelPlay,
      pause:  element.dataset.labelPause,
      mute:   element.dataset.labelMute,
      unmute: element.dataset.labelUnmute,
    };

    this._onClick  = this._onClick.bind(this);
    this._onCancel = this._onCancel.bind(this);
    this._onClose  = this._onClose.bind(this);

    // Los <dialog> viven dentro de la lista, así que un único listener
    // delegado recibe tanto los clicks de las tarjetas como los del popup.
    this._element.addEventListener('click', this._onClick);

    this._element.querySelectorAll('dialog').forEach((dialog) => {
      const video = dialog.querySelector('video');
      if (!video) return;
      dialog.addEventListener('cancel', this._onCancel);
      dialog.addEventListener('close', this._onClose);
      ['play', 'pause', 'ended', 'volumechange'].forEach((type) => {
        video.addEventListener(type, () => this._sync(dialog));
      });
    });
  }

  _onClick(e) {
    const target = e.target;
    const trigger = target.closest(SELECTOR_OPEN);
    if (trigger) {
      this.open(trigger);
      return;
    }

    const dialog = target.closest('dialog');
    if (!dialog) return;

    // Click en el fondo del popup (fuera del video) o en la X.
    if (target === dialog || target === dialog.firstElementChild || target.closest(SELECTOR_CLOSE)) {
      this.close(dialog);
      return;
    }

    const video = dialog.querySelector('video');
    if (target === video || target.closest(SELECTOR_TOGGLE)) {
      if (video.paused) {
        video.play().catch(() => this._sync(dialog));
      } else {
        video.pause();
      }
      return;
    }

    if (target.closest(SELECTOR_MUTE)) {
      video.muted = !video.muted;
    }
  }

  // Esc: se cancela el cierre inmediato del navegador para animarlo.
  _onCancel(e) {
    e.preventDefault();
    this.close(e.currentTarget);
  }

  // Limpieza común a cualquier cierre (animado o forzado por el navegador).
  _onClose(e) {
    const dialog = e.currentTarget;
    const video  = dialog.querySelector('video');
    video.pause();
    video.currentTime = 0;
    dialog.classList.remove(CLASS_OPEN);
    dialog._closing = false;
    document.documentElement.classList.remove(CLASS_HTML_OPEN);
    if (this._trigger) {
      this._trigger.focus({ preventScroll: true });
      this._trigger = null;
    }
  }

  /**
   * @param {HTMLElement} trigger botón de la tarjeta (aria-controls = id del dialog)
   */
  open(trigger) {
    const dialog = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;

    const video = dialog.querySelector('video');
    this._trigger = trigger;

    document.documentElement.classList.add(CLASS_HTML_OPEN);
    dialog.showModal();
    // Doble rAF: el dialog tiene que pintarse en su estado inicial antes de
    // añadir la clase, si no la transición de subida no se ve.
    requestAnimationFrame(() => requestAnimationFrame(() => dialog.classList.add(CLASS_OPEN)));

    video.muted = false;
    video.currentTime = 0;
    video.play().catch(() => this._sync(dialog));
    this._sync(dialog);
  }

  /**
   * @param {HTMLDialogElement} dialog
   */
  close(dialog) {
    if (!dialog?.open || dialog._closing) return;
    dialog._closing = true;
    dialog.querySelector('video').pause();
    dialog.classList.remove(CLASS_OPEN);

    if (prefersReducedMotion()) {
      dialog.close();
      return;
    }

    const panel = dialog.firstElementChild;
    let timer = null;
    const finish = () => {
      clearTimeout(timer);
      panel.removeEventListener('transitionend', onEnd);
      if (dialog.open) dialog.close();
    };
    const onEnd = (e) => {
      if (e.target === panel && e.propertyName === 'transform') finish();
    };
    panel.addEventListener('transitionend', onEnd);
    timer = setTimeout(finish, CLOSE_FALLBACK);
  }

  // Refleja el estado del video en las clases y etiquetas de los controles.
  _sync(dialog) {
    const video  = dialog.querySelector('video');
    const toggle = dialog.querySelector(SELECTOR_TOGGLE);
    const mute   = dialog.querySelector(SELECTOR_MUTE);
    dialog.classList.toggle(CLASS_PLAYING, !video.paused);
    dialog.classList.toggle(CLASS_MUTED, video.muted);
    if (toggle) toggle.setAttribute('aria-label', video.paused ? this._labels.play : this._labels.pause);
    if (mute) mute.setAttribute('aria-label', video.muted ? this._labels.unmute : this._labels.mute);
  }

  /* ============
   * Init masiva
   * ===========*/
  static initAll(root = document) {
    const nodes = root.querySelectorAll(SELECTOR_DATA);
    const instances = [];
    nodes.forEach((el) => {
      if (!el._forbesVideoInstance) el._forbesVideoInstance = new ForbesVideo(el);
      instances.push(el._forbesVideoInstance);
    });
    return instances;
  }
}

export default ForbesVideo;
